/**
 * <cart-drawer>
 *
 * Side drawer (desktop / tablet) and bottom sheet (mobile) for the cart.
 * Overlay, scroll lock, and Escape: slideout-manager.js (shared with modal-slideout).
 */

class CartDrawer extends HTMLElement {
  static overlay = null;

  constructor() {
    super();
    this.isOpen = false;
    this.activeElement = null;
    this._outsideClickHandler = null;
    this._resizeHandler = null;
    this._cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    this.removeAttribute('hidden');
    this.setAnimationForBreakpoint();
    this.moveToBody();
    window.SlideoutManager?.getSharedOverlay();
    this.bindCartIcon();
    this.bindCloseButtons();
    this.bindCartUpdate();

    this._resizeHandler = () => this.setAnimationForBreakpoint();
    window.addEventListener('resize', this._resizeHandler);
  }

  disconnectedCallback() {
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
    if (this._cartUpdateUnsubscriber) this._cartUpdateUnsubscriber();
    this.unbindOpenListeners();
    if (this.isOpen) window.SlideoutManager?.sync();
  }

  setAnimationForBreakpoint() {
    const isMobile = window.utilities?.isMobileBreakpoint?.() ?? window.innerWidth < 964;
    const next = isMobile ? 'slide-bottom' : 'slide-right';
    if (this.getAttribute('animation') !== next) {
      this.setAttribute('animation', next);
    }
  }

  moveToBody() {
    if (this.parentElement !== document.body) {
      const overlay = window.SlideoutManager?.getSharedOverlay();
      if (overlay) {
        document.body.insertBefore(this, overlay);
      } else {
        document.body.appendChild(this);
      }
    }
  }

  bindCartIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble a, #cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', this.id);

    const onActivate = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };

    cartLink.addEventListener('click', onActivate);
    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space' || event.code === 'Enter') onActivate(event);
    });
  }

  bindCloseButtons() {
    this.querySelectorAll('.cart-drawer__close.close-btn').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
  }

  bindCartUpdate() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'cart-drawer-self') return;
      this.refresh();
    });
  }

  bindOpenListeners() {
    this._outsideClickHandler = (e) => {
      if (this.contains(e.target)) return;
      if (e.target.closest('.slideout-modal-overlay')) return;
      if (e.target.closest('modal-slideout[open]')) return;
      this.close();
    };

    document.addEventListener('pointerdown', this._outsideClickHandler);
  }

  unbindOpenListeners() {
    if (this._outsideClickHandler) {
      document.removeEventListener('pointerdown', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    document.querySelectorAll('modal-slideout[open]').forEach((m) => m.close?.());

    this.setAttribute('open', '');
    this.isOpen = true;
    window.SlideoutManager?.sync();

    if (this.activeElement) {
      this.activeElement.setAttribute('aria-expanded', 'true');
    }

    const focusTarget = this.querySelector('.close-btn button, .close-btn, .slideout');
    if (focusTarget) {
      window.utilities?.trapFocus
        ? window.utilities.trapFocus(this, focusTarget)
        : focusTarget.focus({ preventScroll: true });
    }

    this.bindOpenListeners();
  }

  close() {
    this.removeAttribute('open');
    this.isOpen = false;
    window.SlideoutManager?.sync();

    if (this.activeElement) {
      this.activeElement.setAttribute('aria-expanded', 'false');
      try {
        this.activeElement.focus({ preventScroll: true });
      } catch (_) {
        /* noop */
      }
    }

    window.utilities?.removeTrapFocus?.(this.activeElement);
    this.unbindOpenListeners();
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '.slideout' },
      { id: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  renderContents(parsedState) {
    const drawerHTML = parsedState?.sections?.['cart-drawer'];

    if (drawerHTML) {
      document.querySelector('cart-drawer-items')?.removeBuyStackModals?.();

      const newSlideout = this._extract(drawerHTML, '.slideout');
      const oldSlideout = this.querySelector('.slideout');
      if (newSlideout && oldSlideout) {
        oldSlideout.replaceWith(newSlideout);
      }

      const newWrapper = new DOMParser().parseFromString(drawerHTML, 'text/html').querySelector('cart-drawer');
      if (newWrapper) {
        this.classList.toggle('is-empty', newWrapper.classList.contains('is-empty'));
      }
    }

    this._refreshCartIcon(parsedState?.sections?.['cart-icon-bubble']);
    this.bindCloseButtons();
    this.open();
  }

  refresh() {
    fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        document.querySelector('cart-drawer-items')?.removeBuyStackModals?.();

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = parsed.querySelector('cart-drawer');
        const newSlideout = newDrawer?.querySelector('.slideout');
        const oldSlideout = this.querySelector('.slideout');

        if (newSlideout && oldSlideout) {
          oldSlideout.replaceWith(newSlideout);
        }

        if (newDrawer) {
          this.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        }

        this.bindCloseButtons();
      })
      .catch((err) => console.error('cart-drawer refresh failed:', err));
  }

  _extract(sectionHTML, selector) {
    return new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(selector);
  }

  _refreshCartIcon(iconSectionHTML) {
    if (!iconSectionHTML) return;
    const iconEl = document.getElementById('cart-icon-bubble');
    const newInner = this._extract(iconSectionHTML, '.shopify-section')?.innerHTML;
    if (iconEl && newInner) iconEl.innerHTML = newInner;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: 'cart-drawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  bindCustom() {
    this._forceRefreshHandler = async (e) => {
      try {
        await this.itemReplace(e.detail);
      } catch (err) {
        console.error('Error in itemReplace:', err);
      }
    };
    document.addEventListener('cart:force-refresh', this._forceRefreshHandler);
    this.setOriginalCartOrder();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this._forceRefreshHandler) {
      document.removeEventListener('cart:force-refresh', this._forceRefreshHandler);
      this._forceRefreshHandler = null;
    }
  }

  onCartUpdate() {}

  renderCartFromParsedState(parsedState, line, name, variantId, originalVariantId) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const drawerHTML = parsedState?.sections?.['cart-drawer'];
    if (drawerHTML) {
      this.removeBuyStackModals?.();

      const parsed = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const newDrawer = parsed.querySelector('cart-drawer');
      const newSlideout = newDrawer?.querySelector('.slideout');
      const oldSlideout = cartDrawer.querySelector('.slideout');

      if (newSlideout && oldSlideout) {
        oldSlideout.replaceWith(newSlideout);
      }

      if (newDrawer) {
        cartDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }

      cartDrawer.bindCloseButtons?.();

      if (variantId && line) {
        this.restoreCartOrderAfterVariantReplace(line, originalVariantId, variantId);
      } else {
        this.restoreCartDOMOrder();
      }
    }

    const iconHTML = parsedState?.sections?.['cart-icon-bubble'];
    if (iconHTML) {
      const iconEl = document.getElementById('cart-icon-bubble');
      const newInner = new DOMParser()
        .parseFromString(iconHTML, 'text/html')
        .querySelector('.shopify-section')?.innerHTML;
      if (iconEl && newInner) iconEl.innerHTML = newInner;
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-self',
        cartData: parsedState,
      });
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
